import { Button, Card, Grid, Typography, Box, Chip, Tooltip, InputAdornment, IconButton } from "@mui/material";
import { FieldArray, useFormikContext } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { KnowledgeDocumentListItem, AiAgentKnowledgeDocumentDto } from "src/types/knowledgeDocument";
import KnowledgeDocumentRow from "./KnowledgeDocumentRow";
import { addKnowledgeDocument } from "src/utils/knowledgeDocumentHelper";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import { CustomTextField } from "../shared/CustomTextField";
import { KnowledgeSyncStatus } from "src/types/aiAgent";

interface KnowledgeDocumentsSectionProps {
    availableDocuments: KnowledgeDocumentListItem[];
    isLoadingDocuments: boolean;
    onDocumentSelect: (index: number, documentId: string) => void;
    syncStatus?: KnowledgeSyncStatus;
    onRetrySync?: () => void;
}

const KnowledgeDocumentsSection: FC<KnowledgeDocumentsSectionProps> = (props) => {
    const {
        availableDocuments,
        isLoadingDocuments,
        onDocumentSelect,
        syncStatus,
        onRetrySync,
    } = props;
    const { t } = useTranslation();
    const { values, handleChange, handleBlur } = useFormikContext<any>();

    const isSyncInProgress = syncStatus === KnowledgeSyncStatus.InProgress;
    const knowledgeDocuments: AiAgentKnowledgeDocumentDto[] = values.knowledgeDocuments || [];
    const assignedCount = knowledgeDocuments.filter(
        (d: AiAgentKnowledgeDocumentDto) => Boolean(d.knowledgeDocumentId)
    ).length;

    const assignedDocumentIds = knowledgeDocuments
        .filter((d: AiAgentKnowledgeDocumentDto) => Boolean(d.knowledgeDocumentId))
        .map((d: AiAgentKnowledgeDocumentDto) => d.knowledgeDocumentId);

    return (
        <Grid item xs={12}>
            <Card
                sx={{
                    p: 3,
                    mt: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                }}
            >
                {/* Header: title + doc count + sync status */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                            {t("AiAgentList.KnowledgeDocuments.Title")}
                        </Typography>
                        {syncStatus === KnowledgeSyncStatus.Synced && (
                            <Tooltip title={t("AiAgentList.KnowledgeDocuments.SyncStatus.SyncedTooltip")} arrow>
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label={t("AiAgentList.KnowledgeDocuments.SyncStatus.Synced")}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ "& .MuiChip-label": { overflow: "visible" } }}
                                />
                            </Tooltip>
                        )}
                        {syncStatus === KnowledgeSyncStatus.InProgress && (
                            <Tooltip title={t("AiAgentList.KnowledgeDocuments.SyncStatus.InProgressTooltip")} arrow>
                                <Chip
                                    icon={<SyncIcon />}
                                    label={t("AiAgentList.KnowledgeDocuments.SyncStatus.InProgress")}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ "& .MuiChip-label": { overflow: "visible" } }}
                                />
                            </Tooltip>
                        )}
                        {syncStatus === KnowledgeSyncStatus.SyncFailed && (
                            <>
                                <Tooltip title={t("AiAgentList.KnowledgeDocuments.SyncStatus.SyncFailedTooltip")} arrow>
                                    <Chip
                                        icon={<CancelIcon />}
                                        label={t("AiAgentList.KnowledgeDocuments.SyncStatus.SyncFailed")}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        sx={{ "& .MuiChip-label": { overflow: "visible" } }}
                                    />
                                </Tooltip>
                                {onRetrySync && (
                                    <Button
                                        size="small"
                                        startIcon={<RefreshIcon />}
                                        onClick={onRetrySync}
                                        color="error"
                                        variant="text"
                                    >
                                        {t("AiAgentList.KnowledgeDocuments.SyncStatus.Retry")}
                                    </Button>
                                )}
                            </>
                        )}
                        {syncStatus === KnowledgeSyncStatus.NotSynced && (
                            <>
                                <Tooltip title={t("AiAgentList.KnowledgeDocuments.SyncStatus.NotSyncedTooltip")} arrow>
                                    <Chip
                                        icon={<ErrorIcon />}
                                        label={t("AiAgentList.KnowledgeDocuments.SyncStatus.NotSynced")}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                    />
                                </Tooltip>
                                {onRetrySync && (
                                    <Button
                                        size="small"
                                        startIcon={<RefreshIcon />}
                                        onClick={onRetrySync}
                                        color="warning"
                                        variant="text"
                                    >
                                        {t("AiAgentList.KnowledgeDocuments.SyncStatus.Retry")}
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>
                    {assignedCount > 0 && (
                        <Typography variant="body2" color="primary">
                            {assignedCount} {t(assignedCount === 1 ? "AiAgentList.KnowledgeDocuments.DocumentAssigned" : "AiAgentList.KnowledgeDocuments.DocumentsAssigned")}
                        </Typography>
                    )}
                </Box>

                {/* Summary max tokens field — read-only, informational */}
                <Box sx={{ mb: 2, maxWidth: 300 }}>
                    <CustomTextField
                        size="small"
                        color="info"
                        fullWidth
                        label={t("AiAgentList.KnowledgeDocuments.MaxTokenBudget")}
                        name="maxTokenBudget"
                        type="number"
                        value={values.maxTokenBudget}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="outlined"
                        data-testid="maxTokenBudget"
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip
                                        title={t("AiAgentList.KnowledgeDocuments.MaxTokenBudgetTooltip")}
                                        arrow
                                        placement="top"
                                    >
                                        <IconButton size="small" tabIndex={-1}>
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* Document cards */}
                <FieldArray name="knowledgeDocuments" render={(arrayHelpers) => (
                    <>
                        {knowledgeDocuments.map((doc: AiAgentKnowledgeDocumentDto, index: number) => (
                            <KnowledgeDocumentRow
                                key={doc.rowId || index}
                                document={doc}
                                index={index}
                                availableDocuments={availableDocuments}
                                onDelete={() => arrayHelpers.remove(index)}
                                onDocumentSelect={(documentId) => onDocumentSelect(index, documentId)}
                                assignedDocumentIds={assignedDocumentIds}
                                disabled={isSyncInProgress}
                            />
                        ))}
                        <Button
                            color="primary"
                            startIcon={<AddCircleIcon />}
                            variant="contained"
                            onClick={() => addKnowledgeDocument(arrayHelpers)}
                            disabled={isLoadingDocuments || isSyncInProgress}
                            sx={{ mt: 1 }}
                        >
                            {t("Shared.Add")}
                        </Button>
                    </>
                )} />
            </Card>
        </Grid>
    );
};

export default KnowledgeDocumentsSection;
